import xhr from 'live/services/xhr'

export enum Method {
    GET = 'GET',
    POST = 'POST',
    DELETE = 'DELETE',
    PUT = 'PUT'
}

const BASE_URL = '/services/plugin-kpi-rig-floor/rigfloor'
const BASE_LIVE_REST_URL = '/rest'
const BASE_API_ASSETS = '/services/plugin-liverig/assets'

async function sendRequest<T>(url: string, method: string, data?: T): Promise<Response> {
    const options: RequestInit = {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: data !== undefined && method === 'POST' ? JSON.stringify(data) : undefined
    }

    if (data !== undefined) {
        options.body = JSON.stringify(data)
    }

    return await fetch(url, options)
}

export const saveRigFloorConfig = async (
    assetType: string,
    assetId: string,
    operationType: string,
    payload: unknown
): Promise<Response> => {
    return await sendRequest(
        `/services/plugin-kpi-rig-floor/rigfloor/${assetType}/${assetId}/${operationType}/config/`,
        Method.POST,
        payload
    )
}

export const startRigFloorReprocess = async (
    assetId: string,
    isUsingRigFloorInterpolation: boolean,
    interpolationFrequency: number,
    maxDeltaWithoutInterpolation: number,
    start: number,
    end: number,
    hydratongEquipmentUUID?: string,
    hydrarackerEquipmentUUID?: string,
    topDriveEquipmentUUID?: string,
    rrtEquipmentUUID?: string,
    catwalkEquipmentUUID?: string
): Promise<Response> => {
    const params = new URLSearchParams()

    params.append('interpolate', `${isUsingRigFloorInterpolation}`)
    params.append('data_period', `${interpolationFrequency}`)
    params.append('expiration_time', `${maxDeltaWithoutInterpolation}`)
    params.append('start', `${start}`)
    params.append('end', `${end}`)
    params.append('asset_type', 'RIG') // todo: in the future it can't be a constant

    if (hydratongEquipmentUUID) params.append('equipment_hydratong_uuid', hydratongEquipmentUUID)
    if (hydrarackerEquipmentUUID) params.append('equipment_hydraracker_uuid', hydrarackerEquipmentUUID)
    if (topDriveEquipmentUUID) params.append('equipment_top_drive_uuid', topDriveEquipmentUUID)
    if (rrtEquipmentUUID) params.append('equipment_rrt_uuid', rrtEquipmentUUID)
    if (catwalkEquipmentUUID) params.append('equipment_catwalk_uuid', catwalkEquipmentUUID)

    const url = `/services/plugin-kpi-rig-floor/rigfloor/${assetId}/reprocess?${params.toString()}`

    return await sendRequest(url, Method.POST, {})
}

export const startRigFloorRealtime = async (
    assetId: string,
    isUsingRigFloorInterpolation: boolean,
    interpolationFrequency: number,
    maxDeltaWithoutInterpolation: number,
    hydratongEquipmentUUID?: string,
    hydrarackerEquipmentUUID?: string,
    topDriveEquipmentUUID?: string,
    rrtEquipmentUUID?: string,
    catwalkEquipmentUUID?: string
): Promise<Response> => {
    const params = new URLSearchParams()

    params.append('asset_type', 'RIG') // todo: in the future it can't be a constant
    params.append('interpolate', `${isUsingRigFloorInterpolation}`)
    params.append('data_period', `${interpolationFrequency}`)
    params.append('expiration_time', `${maxDeltaWithoutInterpolation}`)

    if (hydratongEquipmentUUID) params.append('equipment_hydratong_uuid', hydratongEquipmentUUID)
    if (hydrarackerEquipmentUUID) params.append('equipment_hydraracker_uuid', hydrarackerEquipmentUUID)
    if (topDriveEquipmentUUID) params.append('equipment_top_drive_uuid', topDriveEquipmentUUID)
    if (rrtEquipmentUUID) params.append('equipment_rrt_uuid', rrtEquipmentUUID)
    if (catwalkEquipmentUUID) params.append('equipment_catwalk_uuid', catwalkEquipmentUUID)

    const url = `/services/plugin-kpi-rig-floor/rigfloor/${assetId}/realtime?${params.toString()}`

    return await sendRequest(url, Method.POST, {})
}

export const stopRigFloorRealtime = async (assetId: string): Promise<Response> => {
    return sendRequest(`/services/plugin-kpi-rig-floor/rigfloor/${assetId}/realtime`, Method.DELETE)
}

export const stopRigFloorReprocessing = async (assetId: string): Promise<Response> => {
    return sendRequest(`/services/plugin-kpi-rig-floor/rigfloor/${assetId}/reprocess`, Method.DELETE)
}

export const fetchRigFloorRealtimeStatus = async (assetId: string): Promise<Response> => {
    return await sendRequest(`/services/plugin-kpi-rig-floor/rigfloor/${assetId}/realtime/status`, Method.GET)
}

export const fetchRigFloorReprocessingStatus = async (assetId: string): Promise<Response> => {
    return await sendRequest(`/services/plugin-kpi-rig-floor/rigfloor/${assetId}/reprocess/status`, Method.GET)
}

export const fetchRigFloorConfig = async (
    assetName: string,
    assetId: string,
    operationType: string
): Promise<Response> => {
    return await sendRequest(
        `/services/plugin-kpi-rig-floor/rigfloor/${assetName}/${assetId}/${operationType}/config/`,
        Method.GET
    )
}

export const fetchEquipments = async (): Promise<Response> => {
    return await sendRequest(`/services/plugin-equipment-manager/equipments-framework/`, Method.GET)
}

export const fetchArchetypes = async (): Promise<Response> => {
    return await sendRequest(`/services/plugin-equipment-manager/equipments-framework/archetype`, Method.GET)
}

export const fetchPreset = async (rigId: string, presetId: string): Promise<Response> => {
    return await sendRequest(`/services/plugin-rig-state/rig-state-model/${rigId}/presets/${presetId}`, Method.GET)
}

/**
 * Checks if there is an active realtime session for the same event type on a different rig.
 * This is used to prevent multiple rigs from running realtime for the same event type simultaneously.
 *
 * @param eventType - The type of drilling event.
 * @param rigId - The ID of the rig being validated.
 * @param wellId - The ID of the well associated with the rig.
 * @returns A Promise resolving with the response indicating whether a conflict exists.
 */
export const hasRealtimeConflict = async (eventType: string, rigId: string, wellId: string): Promise<Response> => {
    return await sendRequest(
        `/services/plugin-kpi-core/kpi/call/kpi-drilling/v1/realtime-on-with-another-rig-same-event`,
        Method.POST,
        { event_type: eventType, data: { rig_id: rigId, well_id: wellId } }
    )
}

export const fetchReprocessQuery = async (payload: any): Promise<Response> => {
    return await sendRequest(`/services/plugin-kpis-drilling/kpireprocess/query`, Method.POST, payload)
}

export enum ModelType {
    HYDRARACKER = 'hydraracker',
    HYDRATONG = 'hydratong',
    KPI_RIG_FLOOR = 'kpi-rigfloor'
}

export const addEvent = async (assetName: string, payload: unknown): Promise<Response> => {
    return await sendRequest(`${BASE_URL}/${assetName}/${ModelType.KPI_RIG_FLOOR}/events/`, Method.POST, payload)
}

export const updateEvent = async (assetName: string, payload: unknown): Promise<Response> => {
    return await sendRequest(`${BASE_URL}/${assetName}/${ModelType.KPI_RIG_FLOOR}/events/`, Method.PUT, payload)
}

export const verifyEvents = async (assetName: string, events: unknown[]): Promise<Response> => {
    return await sendRequest(`${BASE_URL}/${assetName}/${ModelType.KPI_RIG_FLOOR}/events/verify`, Method.POST, events)
}

export const getEventById = async (eventType: string, modelType: ModelType, id: string): Promise<Response> => {
    return await sendRequest(`${BASE_URL}/${eventType}/${modelType}/events/${id}`, Method.GET)
}

export const getRigs = async (): Promise<Response> => {
    return await sendRequest(`${BASE_API_ASSETS}/rig`, Method.GET)
}

export const getEventsBySpan = async (
    eventType: string,
    modelType: ModelType,
    start: number,
    end: number,
    limit: number
): Promise<Response> => {
    return await sendRequest(
        `${BASE_URL}/${eventType}/${modelType}/events/all?start=${start}&end=${end}&limit=${limit}`,
        Method.GET
    )
}

export const deleteEventById = async (eventType: string, modelType: ModelType, id: string): Promise<Response> => {
    return await sendRequest(`${BASE_URL}/${eventType}/${modelType}/events/${id}`, Method.DELETE)
}

export const deleteEventsBySpan = async (
    eventType: string,
    modelType: ModelType,
    start: number,
    end: number
): Promise<Response> => {
    return await sendRequest(
        `${BASE_URL}/${eventType}/${modelType}/events/all?start=${start}&end=${end}`,
        Method.DELETE
    )
}

export const deleteEvents = async (
    eventType: string,
    modelType: ModelType,
    deletedEvents: any[]
): Promise<Response> => {
    return await sendRequest(`${BASE_URL}/${eventType}/${modelType}/events/`, Method.DELETE, deletedEvents)
}

export const updateEvents = async (
    eventType: string,
    modelType: ModelType,
    updatedEvents: any[]
): Promise<Response> => {
    return await sendRequest(`${BASE_URL}/${eventType}/${modelType}/events/`, Method.PUT, updatedEvents)
}

export function getLookupAssetNameEventType(): Promise<Response> {
    return xhr.get(`${BASE_LIVE_REST_URL}/lookup/asset_name_event_type`).then(response => response.data)
}

export const getAvailableKpis = async (assetValue: string, start: string, end: string): Promise<Response> => {
    return await sendRequest('/services/plugin-kpi-rig-floor/kpi-filter/get-available-kpis', Method.POST, {
        assetValue,
        start,
        end
    })
}

export const saveSelectedKpis = async (dashboardId: string, selectedKPIs: string[]): Promise<Response> => {
    return await sendRequest('/services/plugin-kpi-rig-floor/kpi-filter/save-selected-kpis', Method.POST, {
        dashboardId,
        selectedKPIs
    })
}

export const getSelectedKpis = async (dashboardId: string): Promise<Response> => {
    return await sendRequest('/services/plugin-kpi-rig-floor/kpi-filter/get-selected-kpis', Method.POST, {
        dashboardId
    })
}

export const saveCutoffsValues = async (
    dashboardId: string,
    userCutoffsValues: Record<string, unknown>
): Promise<Response> => {
    return await sendRequest('/services/plugin-kpi-rig-floor/cutoffs/save-cutoffs-values', Method.POST, {
        dashboardId,
        userCutoffsValues
    })
}

export const getSelectedCutoffsValues = async (dashboardId: string): Promise<Response> => {
    return await sendRequest('/services/plugin-kpi-rig-floor/cutoffs/get-selected-cutoffs-values', Method.POST, {
        dashboardId
    })
}
